import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { IdentificationCard, Warning, Spinner, CalendarBlank, User, Phone, SealCheck } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = iso => { try { return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }); } catch { return iso; } };

export default function CaretakerVerify() {
  const { bookingId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/checkin/${bookingId}`)
      .then(async r => { if (!r.ok) throw new Error((await r.json()).detail || "Booking not found"); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message));
  }, [bookingId]);

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
        <Warning size={36} weight="fill" className="text-amber-500 mx-auto mb-3" />
        <p className="text-slate-700 font-medium">{error}</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Spinner size={28} className="animate-spin text-amber-500" />
    </div>
  );

  const isPdf = (data.guest_id_url || "").toLowerCase().includes(".pdf");

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 max-w-md w-full space-y-5">
        <div className="text-center">
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-teal-600 bg-teal-50 border border-teal-100 rounded-full px-2.5 py-1 mb-2">
            <IdentificationCard size={12} weight="fill" /> Check-in verification
          </span>
          <h2 className="text-xl font-bold text-slate-800">{data.property_name || "Booking"}</h2>
          {data.property_city && <p className="text-xs text-slate-400 mt-0.5">{data.property_city}</p>}
        </div>

        <div className="bg-slate-50 rounded-2xl p-4 space-y-2.5 text-sm">
          <div className="flex items-center gap-2 text-slate-700"><User size={15} weight="bold" className="text-slate-400" /><span className="font-semibold">{data.guest_name || "Guest"}</span></div>
          {data.guest_phone && <div className="flex items-center gap-2 text-slate-600"><Phone size={15} weight="bold" className="text-slate-400" /><a href={`tel:${data.guest_phone}`} className="font-medium">{data.guest_phone}</a></div>}
          <div className="flex items-center gap-2 text-slate-700"><CalendarBlank size={15} weight="bold" className="text-amber-500" /><span className="font-semibold">{fmt(data.check_in)} → {fmt(data.check_out)}</span><span className="text-slate-400">· {data.guests} guest{data.guests > 1 ? "s" : ""}</span></div>
          {data.platform && <div className="text-[11px] text-slate-400">Source: {data.platform}</div>}
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><IdentificationCard size={14} weight="bold" /> Government ID</p>
          {data.guest_id_url ? (
            isPdf ? (
              <a href={data.guest_id_url} target="_blank" rel="noopener noreferrer"
                className="block text-center py-3 rounded-xl border border-slate-200 text-sm font-semibold text-teal-600 hover:bg-teal-50 transition">
                Open ID document (PDF) →
              </a>
            ) : (
              <a href={data.guest_id_url} target="_blank" rel="noopener noreferrer">
                <img src={data.guest_id_url} alt="Guest ID"
                  className="w-full rounded-xl border border-slate-200 object-contain max-h-80 bg-slate-100" />
                <span className="block text-center text-[11px] text-slate-400 mt-1">Tap to open full size</span>
              </a>
            )
          ) : (
            <div className="text-center bg-amber-50 border border-amber-100 rounded-xl p-4 text-xs text-amber-700">
              No ID on file yet — please collect and verify the guest's government ID at check-in.
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><SealCheck size={14} weight="bold" /> Terms &amp; signature</p>
          {data.signed ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
                <SealCheck size={15} weight="fill" className="text-emerald-500 shrink-0" />
                <span>Guest agreed to the terms &amp; house rules{data.terms_accepted_at ? ` on ${new Date(data.terms_accepted_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}.</span>
              </div>
              {data.signature_url && (
                <a href={data.signature_url} target="_blank" rel="noopener noreferrer">
                  <img src={data.signature_url} alt="Guest signature"
                    className="w-full rounded-xl border border-slate-200 object-contain max-h-32 bg-white" />
                </a>
              )}
            </div>
          ) : (
            <div className="text-center bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700">
              Guest has not signed the agreement yet.
            </div>
          )}
        </div>

        <p className="text-center text-[11px] text-slate-300">
          Powered by <span className="font-bold"><span className="text-slate-400">Host</span><span className="text-amber-500">Folio</span></span>
        </p>
      </motion.div>
    </div>
  );
}
